import React, { useEffect, useState } from 'react';
import { Button, Card } from '@betareader/design-system';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchSwappableBook,
  resetSwappableBook,
  fetchSwappables
} from '../../modules/book';
import { authUtil, imageUtil } from '../../utils';

const DEFAULT_COVER_BACKGROUND = 'https://storage.googleapis.com/beta-reader-prod.appspot.com/defaults/DEFAULT_COVER_BACKGROUND.png';

const InviteToSwap = ({ onSendSwap }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const currentBook = useSelector(state => state.book.currentBook);
  const selectedBook = useSelector(state => state.book.swappableBook);
  const swappables = useSelector(state => state.book.swappables);
  const [existingEntries, setExistingEntries] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const idToken = await authUtil.getFreshIdToken();
        dispatch(fetchSwappables(idToken));
        const response = await fetch(`${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/reader-index/user/${currentBook.author._id}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'br-token': idToken }
        });
        if (!response.ok) throw new Error('Failed to load reader index');
        const user = await response.json();
        if (active) setExistingEntries(user.existingEntries || []);
      } catch (error) {
        console.error(error);
      }
    };
    load();
    return () => {
      active = false;
      dispatch(resetSwappableBook());
    };
  }, [currentBook.author._id, dispatch]);

  const selectBook = async (bookId) => {
    const idToken = await authUtil.getFreshIdToken();
    dispatch(fetchSwappableBook(idToken, bookId));
    setSelectedVersion('');
  };

  const latestVersion = selectedBook?.content?.[selectedBook.content.length - 1]?._id;
  const sendInvitation = () => {
    if (!selectedBook || !(selectedVersion || latestVersion)) return;
    onSendSwap({}, {
      swap: selectedBook._id,
      swapContentVersion: selectedVersion || latestVersion
    });
  };

  return (
    <section style={{ padding: 'var(--br-space-3)' }} aria-label={t('SuggestASwap')}>
      <h3 style={{ marginBottom: 'var(--br-space-1)' }}>{t('SuggestASwap')}</h3>
      <p style={{ color: 'var(--br-muted)' }}>{t('SwapsDontAffectReaderLimit')}</p>
      {selectedBook ? (
        <Card style={{ marginBottom: 'var(--br-space-3)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--br-space-2)' }}>
            <Button variant='ghost' onClick={() => history.push(`/books/${selectedBook._id}`)}>
              {selectedBook.title}
            </Button>
            <Button variant='ghost' aria-label={t('Close')} onClick={() => {
              dispatch(resetSwappableBook());
              setSelectedVersion('');
            }}>×</Button>
          </div>
          <label className='br-field' htmlFor='swap-version'>
            <span className='br-field__label'>{t('Version')}</span>
            <select id='swap-version' className='br-field__input'
              value={selectedVersion || latestVersion || ''}
              onChange={event => setSelectedVersion(event.target.value)}>
              {(selectedBook.content || []).map(version => (
                <option key={version._id} value={version._id}>
                  {t('Version')} {version.versionNumber} · {t('WordCountString', {
                    count: (version.parts || []).reduce((total, part) => total + (part.wordCount || 0), 0)
                  })}
                </option>
              ))}
            </select>
          </label>
          {selectedBook.description && <p><strong>{t('Description')}</strong><br />{selectedBook.description}</p>}
          {selectedBook.feedbackGuidelines && <p><strong>{t('FeedbackGuidelines')}</strong><br />{selectedBook.feedbackGuidelines}</p>}
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--br-space-2)', marginBottom: 'var(--br-space-3)' }}>
          {Array.isArray(swappables) && swappables.filter(book => book.privacy !== 'unpublished').map(book => {
            const alreadyInvited = existingEntries.some(entry => entry.book === book._id);
            return (
              <Card key={book._id} style={{ padding: 'var(--br-space-2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--br-space-2)' }}>
                  <img alt='' src={imageUtil.getImageUrl({
                    originalImageUrl: book?.cover?.highRes?.url ?? DEFAULT_COVER_BACKGROUND,
                    operation: 'fit', size: '50x50'
                  })} style={{ width: 50, height: 50, objectFit: 'cover' }} />
                  <div>
                    <Button variant='ghost' disabled={alreadyInvited}
                      onClick={() => selectBook(book._id)}>{book.title}</Button>
                    {alreadyInvited && <p style={{ color: 'var(--br-muted)', margin: 0 }}>
                      {t('UserAlreadyInvited', { user: currentBook.authorName })}
                    </p>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      {selectedBook && <Button onClick={sendInvitation} disabled={!latestVersion && !selectedVersion}>
        {t('Send')}
      </Button>}
    </section>
  );
};

export default InviteToSwap;
